import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Form, FormField, FormItem } from '@/components/ui/form';
import Title from '@/components/ui/Title';
import { z } from 'zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import ReaddIcon from '@/components/icons/ReaddIcon';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import {
  createQuitPageReason,
  deleteQuitPageReason,
  fetchQuitPage,
  updateQuitPageReason,
  upsertQuitPage,
} from '@/features/webinar/webinarThunks';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import { mapErrorMessage } from '@/lib/mapApiError';
import { cn } from '@/lib/utils';
import ColorSelector from '@/components/ui/ColorSelector';
import { getReadableTextColor } from '@/utils/colors';
import { toWebinarLng } from '@/lib/webinarLanguage';

const getFormSchema = (t: (key: string) => string) =>
  z.object({
    title: z.string().min(1, t('quitPopup.validation.titleRequired')),
    questionText: z.string().min(1, t('quitPopup.validation.questionRequired')),
    type: z.enum(['SINGLE_CHOICE', 'MULTIPLE_CHOICE']),
    options: z.array(
      z.object({
        reason: z.string().min(1, t('quitPopup.validation.optionTitleRequired')),
        explanationText: z.string().optional(),
        imageAlt: z.string().nullable().optional(),
        picture: z.instanceof(File).optional(),
        displayQrCode: z.boolean(),
      }),
    ),
    otherReason: z.boolean(),
    showPopup: z.boolean(),
    backgroundColor: z.string(),
    customBackgroundColor: z.string().optional(),
    textColor: z.string(),
    customTextColor: z.string().optional(),
    buttonColor: z.string(),
    customButtonColor: z.string().optional(),
    buttonTextColor: z.string(),
    customButtonTextColor: z.string().optional(),
  });

type FormValues = z.infer<ReturnType<typeof getFormSchema>>;

const QUIT_ACCORDION_VALUE = 'accordion-0';

export default function QuitForm({
  webinarId,
  workspaceId,
  isEdit,
  onSaveSuccess,
  isEvergreen,
  openAccordionTrigger,
  readOnly,
}: {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
  isEvergreen?: boolean;
  openAccordionTrigger?: number;
  readOnly?: boolean;
}) {
  const locked = readOnly === true;
  const [showDescriptions, setShowDescriptions] = useState<boolean[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<(File | null)[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [existingReasonIds, setExistingReasonIds] = useState<(number | null)[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [otherReasonSelected, setOtherReasonSelected] = useState<boolean>(false);
  const [otherReasonText, setOtherReasonText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [innerAccordionValue, setInnerAccordionValue] = useState<string>('item-2');
  const [outerAccordionValue, setOuterAccordionValue] = useState<string>('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentQuitPage = useAppSelector((s: RootState) => s.webinar.currentQuitPage);
  const quitPageLoading = useAppSelector((s: RootState) => s.webinar.quitPageLoading);
  const visualIdentity = useAppSelector(
    (s: RootState) => s.workspace.currentWorkspace?.visualIdentity,
  ) as WorkspaceVisualIdentity | undefined;
  const { t } = useTranslation('createWebinar');
  const webinarLanguage = useAppSelector((s: RootState) => s.webinar.currentWebinar?.language);
  const webinarLng = toWebinarLng(webinarLanguage);
  const { t: tW } = useTranslation('createWebinar', { lng: webinarLng });

  const theme = visualIdentity?.theme || 'LIGHT';
  const accentColor = visualIdentity?.accentColor || '#2c6cff';
  const baseTextColor = theme === 'DARK' ? '#FFFFFF' : '#000000';
  const baseSecondaryTextColor = theme === 'DARK' ? '#9E9FA6' : '#6D6E7A';

  // Presets de couleurs de l'exit pop-up : on calque le système des notifications
  // (palette identité visuelle + noir/blanc + couleur personnalisée). Pas de dégradé :
  // le back ne stocke qu'un #RRGGBB plat.
  const colorPresets = useMemo(
    () => [
      { id: 'accent', name: t('popupForm.colorNames.accent'), value: visualIdentity?.accentColor },
      {
        id: 'background',
        name: t('popupForm.colorNames.background'),
        value: visualIdentity?.backgroundColor,
      },
      {
        id: 'contrast',
        name: t('popupForm.colorNames.contrast'),
        value: visualIdentity?.contrastColor,
      },
      {
        id: 'secondary-background',
        name: t('popupForm.colorNames.secondaryBackground'),
        value: visualIdentity?.secondaryBackgroundColor,
      },
      { id: 'black', name: t('popupForm.colorNames.black'), value: '#000000' },
      { id: 'white', name: t('popupForm.colorNames.white'), value: '#FFFFFF' },
    ],
    [visualIdentity, t],
  );

  const isCustomColor = (c?: string | null): c is string => !!c && /^#[0-9A-Fa-f]{6}$/.test(c);

  // Preset/custom -> hex envoyé au back (ou null si rien d'exploitable).
  const resolveColor = (id?: string, custom?: string): string | null => {
    if (!id) return null;
    if (id === 'custom') return isCustomColor(custom) ? custom.toUpperCase() : null;
    const preset = colorPresets.find((c) => c.id === id);
    return preset?.value ? preset.value.toUpperCase() : null;
  };

  // Hex du back -> preset correspondant si match identité visuelle, sinon « custom ».
  const mapHexToPreset = (hex?: string | null): { id: string; custom: string } => {
    if (!isCustomColor(hex)) return { id: '', custom: '' };
    const up = hex.toUpperCase();
    const preset = colorPresets.find((c) => c.value && c.value.toUpperCase() === up);
    return preset ? { id: preset.id, custom: '' } : { id: 'custom', custom: up };
  };

  // Valeur nulle côté back = non personnalisé -> on retombe sur le preset qui reproduit
  // le rendu actuel (thème/accent), sans rien imposer aux webinaires existants.
  const defaultTextId = theme === 'DARK' ? 'white' : 'black';
  const defaultButtonTextId = getReadableTextColor(accentColor) === '#FFFFFF' ? 'white' : 'black';
  const initColorIds = (hex: string | null | undefined, fallbackId: string) => {
    const mapped = mapHexToPreset(hex);
    return mapped.id ? mapped : { id: fallbackId, custom: '' };
  };

  const onSubmit = async (data: FormValues) => {
    if (locked) return;
    setIsSubmitting(true);
    try {
      await dispatch(
        upsertQuitPage({
          workspaceId,
          webinarId,
          input: {
            title: data.title,
            questionText: data.questionText,
            type: data.type,
            otherReason: data.otherReason,
            showPopup: data.showPopup,
            backgroundColor: resolveColor(data.backgroundColor, data.customBackgroundColor),
            textColor: resolveColor(data.textColor, data.customTextColor),
            buttonColor: resolveColor(data.buttonColor, data.customButtonColor),
            buttonTextColor: resolveColor(data.buttonTextColor, data.customButtonTextColor),
          },
        }),
      ).unwrap();

      for (let index = 0; index < data.options.length; index++) {
        const option = data.options[index];
        const reasonId = existingReasonIds[index];

        const reasonInput = {
          reason: option.reason,
          explanationText: option.explanationText || '',
          position: index,
          imageAlt: option.imageAlt || null,
          displayQrCode: option.displayQrCode ?? false,
        };

        if (reasonId) {
          await dispatch(
            updateQuitPageReason({
              workspaceId,
              webinarId,
              reasonId,
              input: reasonInput,
            }),
          ).unwrap();
        } else {
          const newReasonId = await dispatch(
            createQuitPageReason({
              workspaceId,
              webinarId,
              input: reasonInput,
            }),
          ).unwrap();

          const newExistingReasonIds = [...existingReasonIds];
          newExistingReasonIds[index] = newReasonId;
          setExistingReasonIds(newExistingReasonIds);
        }
      }
      toast.success(t('quitPopup.messages.saveSuccess'));
      setInnerAccordionValue('');
      if (isEvergreen && isEdit && onSaveSuccess) {
        onSaveSuccess();
      } else if (isEvergreen) {
        navigate(`/create-webinar/chat-evergreen/${webinarId}`);
      } else if (isEdit && onSaveSuccess) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onSaveSuccess();
      } else if (!isEdit) {
        navigate(`/create-webinar/post-event/${webinarId}`);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const err = error as { message?: string; code?: string; status?: number };
      const errorMessage = mapErrorMessage(err?.message, err?.code, err?.status);
      toast.error(t(errorMessage) || t('errors.generic') || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(fetchQuitPage({ workspaceId, webinarId }));
  }, [dispatch, workspaceId, webinarId]);

  const formSchema = useMemo(() => getFormSchema(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title:
        currentQuitPage?.title && currentQuitPage.title.trim() !== ''
          ? currentQuitPage.title
          : tW('quitPopup.defaults.title'),
      questionText:
        currentQuitPage?.questionText && currentQuitPage.questionText.trim() !== ''
          ? currentQuitPage.questionText
          : tW('quitPopup.defaults.questionText'),
      type: (currentQuitPage?.type as 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE') || 'SINGLE_CHOICE',
      options:
        currentQuitPage?.reasons && currentQuitPage.reasons.length > 0
          ? currentQuitPage.reasons.map((reason) => ({
              reason: reason.reason,
              explanationText: reason.explanationText || '',
              imageAlt: reason.imageAlt || null,
              picture: undefined as File | undefined,
              displayQrCode: reason.displayQrCode ?? false,
            }))
          : [
              {
                reason: '',
                explanationText: '',
                imageAlt: null,
                picture: undefined as File | undefined,
                displayQrCode: false,
              },
            ],
      otherReason: currentQuitPage?.otherReason || false,
      showPopup: currentQuitPage?.showPopup ?? false,
      backgroundColor: initColorIds(currentQuitPage?.backgroundColor, 'background').id,
      customBackgroundColor: initColorIds(currentQuitPage?.backgroundColor, 'background').custom,
      textColor: initColorIds(currentQuitPage?.textColor, defaultTextId).id,
      customTextColor: initColorIds(currentQuitPage?.textColor, defaultTextId).custom,
      buttonColor: initColorIds(currentQuitPage?.buttonColor, 'accent').id,
      customButtonColor: initColorIds(currentQuitPage?.buttonColor, 'accent').custom,
      buttonTextColor: initColorIds(currentQuitPage?.buttonTextColor, defaultButtonTextId).id,
      customButtonTextColor: initColorIds(currentQuitPage?.buttonTextColor, defaultButtonTextId)
        .custom,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!currentQuitPage || !isInitialLoad) return;

    const defaultValues: FormValues = {
      title:
        currentQuitPage.title && currentQuitPage.title.trim() !== ''
          ? currentQuitPage.title
          : tW('quitPopup.defaults.titleAlt'),
      questionText:
        currentQuitPage.questionText && currentQuitPage.questionText.trim() !== ''
          ? currentQuitPage.questionText
          : tW('quitPopup.defaults.questionTextAlt'),
      type: (currentQuitPage.type as 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE') || 'SINGLE_CHOICE',
      options:
        currentQuitPage.reasons && currentQuitPage.reasons.length > 0
          ? currentQuitPage.reasons.map((reason) => ({
              reason: reason.reason,
              explanationText: reason.explanationText || '',
              imageAlt: reason.imageAlt || null,
              picture: undefined,
              displayQrCode: reason.displayQrCode ?? false,
            }))
          : [
              {
                reason: tW('quitPopup.defaults.reasons.notInterested'),
                explanationText: '',
                imageAlt: null,
                picture: undefined,
                displayQrCode: false,
              },
              {
                reason: tW('quitPopup.defaults.reasons.mustLeave'),
                explanationText: tW('quitPopup.defaults.reasons.mustLeaveDescription'),
                imageAlt: null,
                picture: undefined,
                displayQrCode: true,
              },
              {
                reason: tW('quitPopup.defaults.reasons.noTime'),
                explanationText: tW('quitPopup.defaults.reasons.noTimeDescription'),
                imageAlt: null,
                picture: undefined,
                displayQrCode: false,
              },
              {
                reason: tW('quitPopup.defaults.reasons.connectionIssues'),
                explanationText: tW('quitPopup.defaults.reasons.connectionIssuesDescription'),
                imageAlt: null,
                picture: undefined,
                displayQrCode: true,
              },
            ],
      otherReason: currentQuitPage.otherReason || false,
      showPopup: currentQuitPage.showPopup ?? true,
      backgroundColor: initColorIds(currentQuitPage.backgroundColor, 'background').id,
      customBackgroundColor: initColorIds(currentQuitPage.backgroundColor, 'background').custom,
      textColor: initColorIds(currentQuitPage.textColor, defaultTextId).id,
      customTextColor: initColorIds(currentQuitPage.textColor, defaultTextId).custom,
      buttonColor: initColorIds(currentQuitPage.buttonColor, 'accent').id,
      customButtonColor: initColorIds(currentQuitPage.buttonColor, 'accent').custom,
      buttonTextColor: initColorIds(currentQuitPage.buttonTextColor, defaultButtonTextId).id,
      customButtonTextColor: initColorIds(currentQuitPage.buttonTextColor, defaultButtonTextId)
        .custom,
    };

    form.reset(defaultValues);
    const shouldShowDescriptions = defaultValues.options.map(
      (option) => !!(option.explanationText && option.explanationText.trim() !== ''),
    );
    setShowDescriptions(shouldShowDescriptions);
    if (currentQuitPage.reasons && currentQuitPage.reasons.length > 0) {
      setExistingImageUrls(currentQuitPage.reasons.map((r) => r.imageUrl ?? ''));
      setExistingReasonIds(currentQuitPage.reasons.map((r) => r.id ?? null));
    } else {
      setExistingImageUrls(defaultValues.options.map(() => ''));
      setExistingReasonIds(defaultValues.options.map(() => null));
    }
    setIsInitialLoad(false);
  }, [currentQuitPage, t, form]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'options',
  });

  const getColorFinal = (fieldName: string): string => {
    const id = form.watch(fieldName as keyof FormValues) as string;
    const customField = `custom${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
    const custom = form.watch(customField as keyof FormValues) as string | undefined;
    return resolveColor(id, custom) || '#FFFFFF';
  };
  const getColorStyle = (colorId: string, customColor?: string) => ({
    backgroundColor: resolveColor(colorId, customColor) || '#FFFFFF',
  });

  // Couleurs résolues pour l'aperçu live (mêmes fallbacks que QuitCard côté spectateur).
  const previewBackgroundColor =
    resolveColor(form.watch('backgroundColor'), form.watch('customBackgroundColor')) ||
    visualIdentity?.backgroundColor ||
    '#FFFFFF';
  const previewTextColor =
    resolveColor(form.watch('textColor'), form.watch('customTextColor')) || baseTextColor;
  const previewSecondaryTextColor =
    resolveColor(form.watch('textColor'), form.watch('customTextColor')) || baseSecondaryTextColor;
  const previewButtonColor =
    resolveColor(form.watch('buttonColor'), form.watch('customButtonColor')) || accentColor;
  const previewButtonTextColor =
    resolveColor(form.watch('buttonTextColor'), form.watch('customButtonTextColor')) || undefined;

  const addOption = () => {
    if (locked) return;
    append({
      reason: '',
      explanationText: '',
      imageAlt: null,
      picture: undefined as File | undefined,
      displayQrCode: false,
    });
    setShowDescriptions([...showDescriptions, false]);
    setSelectedFiles([...selectedFiles, null]);
    setExistingImageUrls([...existingImageUrls, '']);
    setExistingReasonIds([...existingReasonIds, null]);
  };

  const addDescription = (index: number) => {
    if (locked) return;
    const newShowDescriptions = [...showDescriptions];
    newShowDescriptions[index] = !newShowDescriptions[index];
    setShowDescriptions(newShowDescriptions);
  };

  const removeReason = async (index: number) => {
    if (locked) return;
    const reasonId = existingReasonIds[index];

    if (reasonId) {
      try {
        dispatch(
          deleteQuitPageReason({
            workspaceId,
            webinarId,
            reasonId,
          }),
        ).unwrap();

        remove(index);
        const newShowDescriptions = [...showDescriptions];
        const newSelectedFiles = [...selectedFiles];
        const newExistingImageUrls = [...existingImageUrls];
        const newExistingReasonIds = [...existingReasonIds];
        newShowDescriptions.splice(index, 1);
        newSelectedFiles.splice(index, 1);
        newExistingImageUrls.splice(index, 1);
        newExistingReasonIds.splice(index, 1);
        setShowDescriptions(newShowDescriptions);
        setSelectedFiles(newSelectedFiles);
        setExistingImageUrls(newExistingImageUrls);
        setExistingReasonIds(newExistingReasonIds);
      } catch (error) {
        console.error('Erreur lors de la suppression de la raison:', error);
      }
    } else {
      remove(index);
      const newShowDescriptions = [...showDescriptions];
      const newSelectedFiles = [...selectedFiles];
      const newExistingImageUrls = [...existingImageUrls];
      const newExistingReasonIds = [...existingReasonIds];
      newShowDescriptions.splice(index, 1);
      newSelectedFiles.splice(index, 1);
      newExistingImageUrls.splice(index, 1);
      newExistingReasonIds.splice(index, 1);
      setShowDescriptions(newShowDescriptions);
      setSelectedFiles(newSelectedFiles);
      setExistingImageUrls(newExistingImageUrls);
      setExistingReasonIds(newExistingReasonIds);
    }
  };

  const handleOptionSelect = (index: number) => {
    const currentType = form.watch('type');
    if (currentType === 'SINGLE_CHOICE') {
      setSelectedOptions([index]);
      setOtherReasonSelected(false);
    } else {
      setSelectedOptions((prev) =>
        prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
      );
    }
  };

  useEffect(() => {
    setSelectedOptions([]);
    setOtherReasonSelected(false);
  }, [form.watch('type')]);

  useEffect(() => {
    if (openAccordionTrigger != null && openAccordionTrigger > 0) {
      setOuterAccordionValue(QUIT_ACCORDION_VALUE);
    }
  }, [openAccordionTrigger]);

  if (quitPageLoading || isInitialLoad) {
    return (
      <div>
        <Accordion type="single" collapsible defaultValue="accordion-0">
          <AccordionItem value="accordion-1" className="bg-white p-0">
            <AccordionTrigger>
              <Title level={2}>{t('quitPopup.accordionTitle')}</Title>
            </AccordionTrigger>
            <AccordionContent
              className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4' : ''}`}
            >
              <Accordion type="single" collapsible className="space-y-[10px]" defaultValue="item-2">
                <AccordionItem value="item-2" className="bg-medium p-6">
                  <div className="flex flex-col w-full gap-4">
                    <div className="flex items-center gap-2 w-full">
                      <Skeleton className="flex-1 h-10" />
                      <Skeleton className="h-10 w-10 rounded-[20px]" />
                    </div>
                    <Skeleton className="h-10 w-2/3" />
                    <div className="flex items-start gap-2">
                      <Skeleton className="h-10 w-2/3" />
                      <Skeleton className="h-10 w-1/3" />
                    </div>
                    <div className="flex flex-col gap-4">
                      {[1, 2].map((i) => (
                        <div key={i} className="flex flex-col gap-4 pt-2">
                          <div className="flex items-start gap-2">
                            <Skeleton className="flex-1 h-10" />
                            <Skeleton className="h-10 w-10 rounded-lg" />
                            <Skeleton className="h-10 w-10 rounded-lg" />
                          </div>
                          <Skeleton className="h-24 w-full rounded-2xl" />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between py-4">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-6 w-12" />
                    </div>
                    <Skeleton className="h-10 w-full mt-6" />
                  </div>
                </AccordionItem>
              </Accordion>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <Form {...form}>
      <div>
        <Accordion
          type="single"
          collapsible
          {...(openAccordionTrigger != null
            ? { value: outerAccordionValue, onValueChange: setOuterAccordionValue }
            : { defaultValue: '' })}
          className="rounded-[20px]"
        >
          <AccordionItem
            value={QUIT_ACCORDION_VALUE}
            className={`bg-white p-0 rounded-[20px] ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger
              fillForm={
                currentQuitPage?.title && currentQuitPage.title.trim() !== '' ? true : false
              }
              className="rounded-[20px]"
            >
              <div className="flex flex-col gap-2">
                <Title level={2}>{t('quitPopup.accordionTitle')}</Title>
                <p className="text-sm font-normal text-tertiary">
                  {t('quitPopup.accordionDescription')}
                </p>
              </div>
            </AccordionTrigger>
            <FormField
              name="showPopup"
              control={form.control}
              render={({ field }) => (
                <div className="pb-4 flex items-center justify-between w-full">
                  <Label>{t('quitPopup.enablePopupLabel')}</Label>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={locked}
                  />
                </div>
              )}
            />
            <AccordionContent
              className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4' : ''}`}
            >
              <Accordion
                type="single"
                collapsible
                className="space-y-[10px]"
                value={innerAccordionValue}
                onValueChange={(value) => setInnerAccordionValue(value || '')}
              >
                <AccordionItem key="item-2" value="item-2" className="bg-medium p-6 rounded-[20px]">
                  <div className="flex items-center gap-2 w-full">
                    <div className="flex-1 ">
                      <FormField
                        name={`title`}
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <TextFieldWithLimit
                              className="bg-white"
                              label={t('quitPopup.popupTitleLabel')}
                              value={field.value ?? ''}
                              onChange={(v) => field.onChange(v)}
                              disabled={locked}
                              maxLength={150}
                              error={fieldState.error?.message}
                            />
                          </FormItem>
                        )}
                      />
                    </div>
                    <AccordionTrigger className="rounded-[20px] p-0 "></AccordionTrigger>
                  </div>
                  {/* <AccordionTrigger className="rounded-[20px] p-0 ">
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex-1 mr-2">
                        <FormField
                          name={`title`}
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <InputLabel
                                className="bg-white"
                                label={t('quitPopup.popupTitleLabel')}
                                value={field.value}
                                onChange={field.onChange}
                              />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </AccordionTrigger> */}
                  <AccordionContent>
                    <div className="flex flex-col w-full">
                      <Title level={2} className="py-4">
                        {t('quitPopup.questionnaireTitle')}
                      </Title>

                      <div
                        key="questionText"
                        className="flex flex-col lg:flex-row items-start gap-2"
                      >
                        <div className="w-full lg:w-2/3">
                          <FormField
                            name={`questionText`}
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <FormItem>
                                <TextFieldWithLimit
                                  className="bg-white"
                                  label={t('quitPopup.questionLabel')}
                                  value={field.value ?? ''}
                                  onChange={(v) => field.onChange(v)}
                                  disabled={locked}
                                  maxLength={300}
                                  error={fieldState.error?.message}
                                />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="w-full lg:w-1/3 flex flex-col">
                          <FormField
                            name={`type`}
                            control={form.control}
                            render={({ field }) => (
                              <FormItem>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  disabled={locked}
                                >
                                  <SelectTrigger className="h-14" label={t('quitPopup.typeLabel')}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="SINGLE_CHOICE">
                                      {t('quitPopup.type.single')}
                                    </SelectItem>
                                    <SelectItem value="MULTIPLE_CHOICE">
                                      {t('quitPopup.type.multiple')}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                          {/* Espace invisible pour maintenir l'alignement */}
                          <div className="h-5"></div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4">
                        {fields.map((field, index) => (
                          <div key={field.id} className="flex flex-col gap-6">
                            <div className="flex flex-col gap-4 pt-2">
                              <div className="flex items-start gap-2">
                                <FormField
                                  name={`options.${index}.reason`}
                                  control={form.control}
                                  render={({ field, fieldState }) => (
                                    <FormItem className="flex-1">
                                      <TextFieldWithLimit
                                        className="bg-white"
                                        label={t('quitPopup.optionLabel', { index: index + 1 })}
                                        value={field.value ?? ''}
                                        onChange={(v) => field.onChange(v)}
                                        disabled={locked}
                                        maxLength={150}
                                        error={fieldState.error?.message}
                                      />
                                    </FormItem>
                                  )}
                                />
                                {fields.length > 1 && !locked && (
                                  <button
                                    type="button"
                                    onClick={() => removeReason(index)}
                                    className="bg-white h-14 rounded-lg  py-3 px-2 border border-input cursor-pointer"
                                  >
                                    <Trash2 className="size-6" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  disabled={locked}
                                  className={`py-3 px-2 h-14 rounded-lg ${showDescriptions[index] ? 'bg-[#95B6FF]' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                                  onClick={() => addDescription(index)}
                                >
                                  <ReaddIcon className="size-6" />
                                </button>
                              </div>

                              {showDescriptions[index] && (
                                <>
                                  <FormField
                                    name={`options.${index}.explanationText`}
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                      <FormItem>
                                        <TextFieldWithLimit
                                          className="bg-white"
                                          label={t('quitPopup.optionDescriptionLabel', {
                                            index: index + 1,
                                          })}
                                          value={field.value || ''}
                                          onChange={(v) => field.onChange(v)}
                                          disabled={locked}
                                          maxLength={1000}
                                          error={fieldState.error?.message}
                                        />
                                      </FormItem>
                                    )}
                                  />

                                  <div className="flex items-center justify-between py-2">
                                    <Label>
                                      {t('quitPopup.displayQrCodeSwitch', {
                                        defaultValue: 'Afficher le QR code',
                                      })}
                                    </Label>
                                    <FormField
                                      name={`options.${index}.displayQrCode`}
                                      control={form.control}
                                      render={({ field }) => (
                                        <Switch
                                          checked={field.value}
                                          onCheckedChange={field.onChange}
                                          disabled={locked}
                                        />
                                      )}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between py-4">
                        <Label>{t('quitPopup.otherReasonSwitch')}</Label>
                        <FormField
                          name="otherReason"
                          control={form.control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={locked}
                            />
                          )}
                        />
                      </div>

                      {fields.length < 5 && !locked && (
                        <button
                          type="button"
                          onClick={() => addOption()}
                          className="flex items-center gap-2"
                        >
                          <Plus className="size-6" />
                          <Label className="cursor-pointer text-sm">
                            {t('quitPopup.addOption')}
                          </Label>
                        </button>
                      )}

                      <Separator className="bg-input my-4" />

                      <div
                        className={cn(
                          'flex flex-col gap-4',
                          locked && 'pointer-events-none select-none opacity-90',
                        )}
                      >
                        <Title level={2}>{t('quitPopup.colorsTitle')}</Title>
                        <ColorSelector
                          form={form}
                          offerType="A"
                          colorsArray={colorPresets}
                          colorFieldName="backgroundColor"
                          customColorFieldName="customBackgroundColor"
                          label={t('quitPopup.backgroundColorLabel')}
                          customColorLabel={t('popupForm.customColor')}
                          getFinalColor={getColorFinal}
                          getStyle={getColorStyle}
                        />
                        <ColorSelector
                          form={form}
                          offerType="A"
                          colorsArray={colorPresets}
                          colorFieldName="textColor"
                          customColorFieldName="customTextColor"
                          label={t('quitPopup.textColorLabel')}
                          customColorLabel={t('popupForm.customColor')}
                          getFinalColor={getColorFinal}
                          getStyle={getColorStyle}
                        />
                        <ColorSelector
                          form={form}
                          offerType="A"
                          colorsArray={colorPresets}
                          colorFieldName="buttonColor"
                          customColorFieldName="customButtonColor"
                          label={t('quitPopup.buttonColorLabel')}
                          customColorLabel={t('popupForm.customColor')}
                          getFinalColor={getColorFinal}
                          getStyle={getColorStyle}
                        />
                        <ColorSelector
                          form={form}
                          offerType="A"
                          colorsArray={colorPresets}
                          colorFieldName="buttonTextColor"
                          customColorFieldName="customButtonTextColor"
                          label={t('quitPopup.buttonTextColorLabel')}
                          customColorLabel={t('popupForm.customColor')}
                          getFinalColor={getColorFinal}
                          getStyle={getColorStyle}
                        />
                      </div>

                      <Separator className="bg-input my-4" />

                      <Button
                        type="button"
                        variant="default"
                        className="w-full disabled:bg-gray-200 disabled:text-gray-500"
                        disabled={locked || isSubmitting || !form.formState.isValid}
                        onClick={() => form.handleSubmit(onSubmit)()}
                      >
                        {isSubmitting && <Loader />}
                        {t('quitPopup.save')}
                      </Button>

                      <div className="flex flex-col gap-4 pt-8">
                        <Title level={2}>{t('quitPopup.previewTitle')}</Title>

                        <div
                          className={cn(
                            'flex flex-col gap-6 rounded-lg p-4 w-full lg:w-[600px] mx-auto',
                            locked && 'pointer-events-none select-none opacity-90',
                          )}
                          style={{
                            backgroundColor: previewBackgroundColor,
                          }}
                        >
                          <h1
                            className="text-[32px]/[40px] font-semibold"
                            style={{
                              color: previewTextColor,
                              fontFamily: visualIdentity?.fontFamily || 'Poppins',
                            }}
                          >
                            {form.watch('title')}
                          </h1>
                          <p
                            className="text-sm"
                            style={{
                              color: previewSecondaryTextColor,
                              fontFamily: visualIdentity?.fontFamily || 'Poppins',
                            }}
                          >
                            {form.watch('questionText')}
                          </p>
                          <div className="flex flex-col gap-4">
                            {form.watch('type') === 'SINGLE_CHOICE' ? (
                              <RadioGroup
                                value={
                                  selectedOptions.length > 0 ? `option-${selectedOptions[0]}` : ''
                                }
                                onValueChange={(value) => {
                                  if (value) {
                                    const index = parseInt(value.replace('option-', ''));
                                    setSelectedOptions([index]);
                                  }
                                }}
                              >
                                {fields.map((field, index) => (
                                  <div key={field.id} className="flex flex-col gap-4">
                                    <div
                                      className="flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer"
                                      onClick={() => handleOptionSelect(index)}
                                    >
                                      <RadioGroupItem
                                        value={`option-${index}`}
                                        id={`option-${index}`}
                                        checked={selectedOptions.includes(index)}
                                        style={{
                                          borderColor:
                                            visualIdentity?.secondaryBackgroundColor || '#E5E7EB',
                                        }}
                                      />
                                      <label
                                        htmlFor={`option-${index}`}
                                        className="cursor-pointer flex-1"
                                        style={{
                                          color: previewTextColor,
                                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                        }}
                                      >
                                        {form.watch(`options.${index}.reason`)}
                                      </label>
                                    </div>
                                    {selectedOptions.includes(index) && (
                                      <>
                                        {showDescriptions[index] &&
                                          form.watch(`options.${index}.explanationText`) && (
                                            <p
                                              className="text-sm"
                                              style={{
                                                color: previewSecondaryTextColor,
                                                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                              }}
                                            >
                                              {form.watch(`options.${index}.explanationText`)}
                                            </p>
                                          )}
                                        {form.watch(`options.${index}.displayQrCode`) && (
                                          <div className="flex items-center justify-center">
                                            <img
                                              src="/assets/qrcode.png"
                                              alt="QR code"
                                              className="max-w-xs max-h-32 object-cover rounded-lg"
                                            />
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                                {form.watch('otherReason') && (
                                  <div className="flex flex-col gap-4">
                                    <div
                                      className="flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer"
                                      onClick={() => {
                                        if (form.watch('type') === 'SINGLE_CHOICE') {
                                          setOtherReasonSelected(true);
                                          setSelectedOptions([]);
                                        } else {
                                          setOtherReasonSelected(!otherReasonSelected);
                                        }
                                      }}
                                    >
                                      <RadioGroupItem
                                        value="other-reason"
                                        id="other-reason"
                                        checked={otherReasonSelected}
                                        style={{
                                          borderColor:
                                            visualIdentity?.secondaryBackgroundColor || '#E5E7EB',
                                        }}
                                      />
                                      <label
                                        htmlFor="other-reason"
                                        className="cursor-pointer flex-1"
                                        style={{
                                          color: previewTextColor,
                                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                        }}
                                      >
                                        {tW('quitPopup.otherReason')}
                                      </label>
                                    </div>
                                    {otherReasonSelected && (
                                      <div>
                                        <textarea
                                          placeholder={tW('quitPopup.otherReasonPlaceholder')}
                                          value={otherReasonText}
                                          onChange={(e) => setOtherReasonText(e.target.value)}
                                          className="w-full p-3 border border-input rounded-lg resize-none"
                                          rows={3}
                                          style={{
                                            color: previewTextColor,
                                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </RadioGroup>
                            ) : (
                              <>
                                {fields.map((field, index) => (
                                  <div key={field.id} className="flex flex-col gap-4">
                                    <div
                                      className="flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer"
                                      onClick={() => handleOptionSelect(index)}
                                    >
                                      <Checkbox
                                        id={`checkbox-${index}`}
                                        checked={selectedOptions.includes(index)}
                                        style={{
                                          borderColor:
                                            visualIdentity?.secondaryBackgroundColor || undefined,
                                        }}
                                      />
                                      <label
                                        htmlFor={`checkbox-${index}`}
                                        className="cursor-pointer flex-1"
                                        style={{
                                          color: previewTextColor,
                                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                        }}
                                      >
                                        {form.watch(`options.${index}.reason`)}
                                      </label>
                                    </div>
                                    {selectedOptions.includes(index) && (
                                      <>
                                        {showDescriptions[index] &&
                                          form.watch(`options.${index}.explanationText`) && (
                                            <p
                                              className="text-sm"
                                              style={{
                                                color: previewSecondaryTextColor,
                                                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                              }}
                                            >
                                              {form.watch(`options.${index}.explanationText`)}
                                            </p>
                                          )}
                                        {form.watch(`options.${index}.displayQrCode`) && (
                                          <div className="flex items-center justify-center">
                                            <img
                                              src="/assets/qrcode.png"
                                              alt="QR code"
                                              className="max-w-xs max-h-32 object-cover rounded-lg"
                                            />
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                                {form.watch('otherReason') && (
                                  <div className="flex flex-col gap-4">
                                    <div
                                      className="flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer"
                                      onClick={() => {
                                        setOtherReasonSelected(!otherReasonSelected);
                                      }}
                                    >
                                      <Checkbox
                                        id="checkbox-other-reason"
                                        checked={otherReasonSelected}
                                        style={{
                                          borderColor:
                                            visualIdentity?.secondaryBackgroundColor || '#E5E7EB',
                                        }}
                                      />
                                      <label
                                        htmlFor="checkbox-other-reason"
                                        className="cursor-pointer flex-1"
                                        style={{
                                          color: previewTextColor,
                                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                        }}
                                      >
                                        {tW('quitPopup.otherReason')}
                                      </label>
                                    </div>
                                    {otherReasonSelected && (
                                      <div>
                                        <textarea
                                          placeholder={tW('quitPopup.otherReasonPlaceholder')}
                                          value={otherReasonText}
                                          onChange={(e) => setOtherReasonText(e.target.value)}
                                          className="w-full p-3 border border-input rounded-lg resize-none"
                                          rows={3}
                                          style={{
                                            color: previewTextColor,
                                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-4 w-full">
                            <div className="w-1/2">
                              <Button
                                variant="outline"
                                className="w-full"
                                style={{
                                  borderColor: previewButtonColor,
                                  color: previewButtonColor,
                                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                }}
                              >
                                {tW('quitPopup.previewButtons.cancel')}
                              </Button>
                            </div>
                            <div className="w-1/2">
                              <Button
                                variant="default"
                                className="w-full"
                                style={{
                                  // Aperçu non interactif : on applique les couleurs choisies pour
                                  // que le client voie le rendu réel du bouton principal.
                                  backgroundColor: previewButtonColor,
                                  color: previewButtonTextColor,
                                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                }}
                              >
                                {tW('quitPopup.previewButtons.saveAndQuit')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </Form>
  );
}
